import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { EDUCATION_LEVEL } from "src/assets/files/resources/education-level";

import { Observable, debounceTime, filter, map, switchMap, tap } from "rxjs";
import { AppToastService } from "src/app/core/services/app-toast.service";
import { AuthenticationService } from "src/app/core/services/auth.service";
import { CompanyService } from "src/app/core/services/company.service";
import { EnumsService } from "src/app/core/services/enums.service";
import { cpfValidator } from "src/app/shared/directives/cpf-validator.directive";
import { phoneValidator } from "src/app/shared/directives/phone-validator.directive";
import { minimumAgeValidator } from "src/app/shared/directives/minimum-age.directive";

@Component({
  selector: "app-personal-bank-details",
  templateUrl: "./personal-bank-details.component.html",
  styleUrls: ["./personal-bank-details.component.scss"],
})
export class PersonalBankDetailsComponent implements OnInit, OnChanges {
  martialStatusArray = [];

  genderArray = [
    { name: "Masculino", value: 1 },
    { name: "Feminino", value: 2 },
    { name: "Não binário(a)", value: 3 },
    { name: "Fluido(a) de gênero", value: 4 },
    { name: "Bigênero", value: 5 },
  ];

  accountTypeArray = [
    { name: "Desconhecido", value: 0 },
    { name: "Conta Corrente", value: 1 },
    { name: "Conta Poupança", value: 2 },
    { name: "Conta de Pagamento", value: 3 },
    { name: "Conta Salário", value: 4 },
  ];

  schooling = EDUCATION_LEVEL;

  form: UntypedFormGroup;
  phoneDirty = false;

  @Input() isContributorProfile = false;
  @Input() isExhibition = false;
  @Input() getPersonalBankDetails;
  @Input() bankList;

  @Output() personalBankDetailsValue: EventEmitter<any> =
    new EventEmitter<any>();
  @Output() currentStepIndexChange = new EventEmitter<number>();
  @Output() previousStepIndexChange = new EventEmitter<void>();

  countries: any[];
  cities: any[];
  raceColors$: Observable<any[]>;
  foreignStatus$: Observable<any[]>;
  retiredTypes$: Observable<any[]>;
  pcdTypes$: Observable<any[]>;

  get f() {
    return this.form.controls;
  }

  get formValue() {
    return JSON.stringify(this.form.getRawValue());
  }

  get isConsult() {
    return this.authService.IsConsult;
  }

  constructor(
    private formBuilder: FormBuilder,
    private service: CompanyService,
    private toast: AppToastService,
    private enumsService: EnumsService,
    private authService: AuthenticationService,
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.initCep();
    this.initCountrySelect();
    this.initCitySelect();

    this.enumsService
      .getMaritalType()
      .subscribe((res) => (this.martialStatusArray = res));

    this.raceColors$ = this.enumsService.getRaceColor();
    this.foreignStatus$ = this.enumsService.getForeignStatus();
    this.retiredTypes$ = this.enumsService.getRetiredType();
    this.pcdTypes$ = this.enumsService.getPcdType();
  }

  updatePhoneMask(el: string, value: string, type: string = ""): string {
    if (type === "required") {
      this.setEmergencyContactRequired();
    }

    if (!value) {
      return this.updateData(el, "");
    }

    let digits = value.replace(/\D/g, "");
    digits = digits.slice(0, 11);

    if (digits.length === 0) {
      return this.updateData(el, "");
    }

    if (digits.length === 11) {
      const phone = digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
      return this.updateData(el, phone);
    }

    if (digits.length === 10) {
      const phone = digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
      return this.updateData(el, phone);
    }

    return this.updateData(el, digits);
  }

  updateEmergencyText(el: string, value: string, type: string = ""): string {
    if (type === "required") {
      this.setEmergencyContactRequired();
    }

    if (!value) {
      return this.updateData(el, "");
    }

    const cleaned = value.replace(/[^A-Za-zÀ-ÿ\s]/g, "");
    const normalized = cleaned.replace(/\s+/g, " ");
    return this.updateData(el, normalized);
  }

  setEmergencyContactRequired() {
    const body = this.form.value;

    const name = body.emergencyContactName2 ?? "";
    const phone = body.emergencyContactPhone2 ?? "";
    const kinship = body.emergencyContactKinship2 ?? "";

    const fields = [
      { len: name.length, name: "emergencyContactName2" },
      { len: phone.length, name: "emergencyContactPhone2" },
      { len: kinship.length, name: "emergencyContactKinship2" }
    ];

    let phoneHasValidate = false;

    fields.forEach(x => { this.form.get(x.name)?.clearValidators(); })

    if (
      (name.length > 0 && (phone.length === 0 || kinship.length === 0)) ||
      (phone.length > 0 && (name.length === 0 || kinship.length === 0)) ||
      (kinship.length > 0 && (name.length === 0 || phone.length === 0))
    ) {
      fields.forEach(x => {
        if (x.name === "emergencyContactPhone2") {
          phoneHasValidate = true;
          this.form.get(x.name)?.setValidators([Validators.required, phoneValidator()]);
          return;
        }

        if (x.len === 0) {
          this.form.get(x.name)?.setValidators([Validators.required]);
        }
      });
    }

    if (!phoneHasValidate) {
      this.form.get("emergencyContactPhone2")?.setValidators([phoneValidator()]);
    }

    fields.forEach(x => { this.form.get(x.name)?.updateValueAndValidity(); })
  }

  updateData(el: string, data: string): string {
    this.form.get(el)?.setValue(data);
    return "dado atualizado";
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadForm(changes);
  }

  loadForm(changes: SimpleChanges) {
    const data = changes.getPersonalBankDetails?.currentValue;

    if (data) {
      this.form?.patchValue({
        ...data,
        isForeignMarried: data.foreignIsMarried,
      });
    }
  }

  createForm() {
    this.form = this.formBuilder.group({
      pis: ["", Validators.required],
      phone: [""],
      name: [{ value: "", disabled: this.isExhibition }],
      gender: [{ value: "", disabled: this.isExhibition }, Validators.required],
      birthdate: [
        { value: "", disabled: this.isExhibition },
        [Validators.required, minimumAgeValidator(14)],
      ],
      maritalStatus: [
        { value: "", disabled: this.isExhibition },
        Validators.required,
      ],
      cpf: [
        { value: "", disabled: this.isExhibition },
        [Validators.required, cpfValidator()],
      ],
      rg: [{ value: "", disabled: this.isExhibition }, Validators.required],
      isPcd: [{ value: false, disabled: this.isExhibition }],
      height: [{ value: "", disabled: this.isExhibition }],
      weight: [{ value: null, disabled: this.isExhibition }],
      linkedin: [{ value: "", disabled: this.isExhibition }],
      postalCode: [
        { value: "", disabled: this.isExhibition },
        Validators.required,
      ],
      address: [
        { value: "", disabled: this.isExhibition },
        Validators.required,
      ],
      number: [{ value: "", disabled: this.isExhibition }],
      neighborhood: [
        { value: "", disabled: this.isExhibition },
        Validators.required,
      ],
      stateId: [
        { value: "", disabled: this.isExhibition },
        Validators.required,
      ],
      cityId: [{ value: "", disabled: this.isExhibition }, Validators.required],
      complement: [{ value: "", disabled: this.isExhibition }],
      bankId: [{ value: "", disabled: this.isExhibition }, Validators.required],
      agency: [
        { value: null, disabled: this.isExhibition },
        Validators.required,
      ],
      agencyDigit: [{ value: null, disabled: this.isExhibition }],
      account: [
        { value: null, disabled: this.isExhibition },
        Validators.required,
      ],
      accountDigit: [
        { value: null, disabled: this.isExhibition },
        Validators.required,
      ],
      accountType: [
        { value: null, disabled: this.isExhibition },
        Validators.required,
      ],
      schoolingId: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      motherName: ["", Validators.required],
      fatherName: "",
      motherNationality: null,
      fatherNationality: null,
      raceColorId: null,
      isStudent: null,
      retiredTypeId: null,
      foreignStatusId: null,
      foreignRne: "",
      foreignRneShippingDate: null,
      foreignRneIssuingAgency: null,
      foreignRneDueDate: null,
      isForeignMarried: null,
      hasForeignChild: null,
      foreignPcdId: null,
      ctpsShippingDate: null,
      reservistNumber: null,
      ricIdentity: null,
      ricIssuingAgency: null,
      ricShippingDate: null,
      emergencyContactName: ["", Validators.required],
      emergencyContactPhone: ["", [Validators.required, phoneValidator()]],
      emergencyContactKinship: ["", Validators.required],
      emergencyContactName2: null,
      emergencyContactPhone2: null,
      emergencyContactKinship2: null,
    });
  }

  updateState(stateId: string) {
    if (!stateId) {
      this.f.cityId.reset(null, { emitEvent: false });
      this.f.stateId.reset(null, { emitEvent: false });
    }

    this.f.stateId.setValue(stateId, { emitEvent: false });
    this.f.cityId.setValue(null, { emitEvent: false });

    return this.service.getAllCities(stateId, " ").pipe(
      debounceTime(500),
      map((cities: any) => {
        this.cities = [...cities];
        return this.cities;
      }),
    );
  }

  initCep() {
    this.f.postalCode.valueChanges
      .pipe(
        filter((value) => value?.length === 8),
        debounceTime(500),
        switchMap((value: string) => this.service.findAddress(value)),
      )
      .subscribe((cepData: any) => {
        if (cepData?.erro) {
          this.toast.show("busca por cep", "cep não encontrado", false);
          return;
        }
        if (cepData) {
          this.f.address.setValue(cepData.logradouro);
          this.f.neighborhood.setValue(cepData.bairro);
          this.service
            .getAllCountries(cepData.uf)
            .pipe(
              switchMap((countryResult: any) =>
                this.updateState(countryResult[0]?.id),
              ),
              tap((cities: any) => {
                const city = cities.find((x) =>
                  x.name
                    .toLowerCase()
                    .includes(cepData.localidade.toLowerCase()),
                );

                if (city) this.f.cityId.setValue(city.id, { emitEvent: false });
              }),
            )
            .subscribe();
        }
      });
  }

  initCitySelect() {
    if (this.f.stateId.value != null) {
      this.service.getAllCities(this.f.stateId.value, " ").subscribe();
    }

    this.f.stateId.valueChanges
      .pipe(switchMap((stateId: string) => this.updateState(stateId)))
      .subscribe();
  }

  initCountrySelect() {
    this.service
      .getAllCountries(" ")
      .subscribe((data: any) => (this.countries = [...data]));
  }

  next(logout?) {
    if (this.form.invalid) {
      Object.keys(this.f).forEach((c) => this.f[c].markAsDirty());
      this.toast.show(
        "Validação de formulário",
        "Informações obrigatórias não preenchidas. Revise os campos e salve novamente.",
      );
      return false;
    }

    const body = this.form.value;

    this.personalBankDetailsValue.emit({
      body: {
        ...body,
        weight: body.weight || null,
        height: body.height || null,
      },
      logout,
    });
    return true;
  }

  back() {
    this.previousStepIndexChange.emit();
  }
}
